import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Map } from '@vis.gl/react-maplibre';
import type { MapEvent, MapLayerMouseEvent, MapRef, StyleSpecification } from '@vis.gl/react-maplibre';
import { MapBearingContext, MapZoomContext } from './MapBearingContext';
import { CameraController, type NarrativeMeta } from './CameraController';
import { NarrativeOverlay } from './NarrativeOverlay';
import { TrafficLayer } from './TrafficLayer';
import type { FlowTileStyle } from '../../types/traffic';
import { useEditMode } from '../../store/editMode';

const STREET_STYLE = 'https://tiles.openfreemap.org/styles/liberty';

const SATELLITE_STYLE: StyleSpecification = {
  version: 8,
  sources: {
    'esri-satellite': {
      type: 'raster',
      tiles: [
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
      ],
      tileSize: 256,
      maxzoom: 19,
    }
  },
  layers: [{ id: 'satellite', type: 'raster', source: 'esri-satellite' }]
};

const HIDDEN_LAYERS = [
  'park', 'park_outline',
  'landcover_wood', 'landcover_grass', 'landcover_wetland',
  'poi_r20', 'poi_r7', 'poi_r1'
];

interface MapViewProps {
  children?: ReactNode;
  center?: [number, number];
  zoom?: number;
  bearing?: number;
  pitch?: number;
  maxBounds?: [[number, number], [number, number]];
  minZoom?: number;
  maxZoom?: number;
  cameraControl?: boolean;
  onCameraAlarm?: (alarm: { driverId: number; code: string } | null) => void;
  onAlarmMeta?: (meta: NarrativeMeta | null) => void;
  onViewState?: (center: [number, number], zoom: number, bearing: number, pitch: number) => void;
}

const DEFAULT_CENTER: [number, number] = [2.1651, 41.3908];
const DEFAULT_ZOOM = 18.5;
const DEFAULT_BEARING = -45;
const DEFAULT_PITCH = 50;

export function MapView({
  children,
  center = DEFAULT_CENTER,
  zoom = DEFAULT_ZOOM,
  bearing = DEFAULT_BEARING,
  pitch = DEFAULT_PITCH,
  maxBounds,
  minZoom,
  maxZoom,
  cameraControl = false,
  onCameraAlarm,
  onAlarmMeta,
  onViewState,
}: MapViewProps) {
  const [mapBearing, setMapBearing] = useState(bearing);
  const [mapZoom, setMapZoom] = useState(zoom);
  const [satellite, setSatellite] = useState(false);
  const [flowStyle, setFlowStyle] = useState<FlowTileStyle>('relative0');
  const [mapReady, setMapReady] = useState(false);
  const [infoMeta, setInfoMeta] = useState<NarrativeMeta | null>(null);
  const [trafficAvailable, setTrafficAvailable] = useState(true);
  const mapRef = useRef<MapRef>(null);

  // Probe TomTom tile API on mount
  useEffect(() => {
    const key = import.meta.env.VITE_TOMTOM_API_KEY as string | undefined;
    if (!key) return;
    fetch(`https://api.tomtom.com/traffic/map/4/tile/flow/relative0/12/2020/1545.png?key=${key}&tileSize=512`, { method: 'HEAD' })
      .then(r => { if (!r.ok) setTrafficAvailable(false); })
      .catch(() => setTrafficAvailable(false));
  }, []);

  const mapStyle = useMemo(() => satellite ? SATELLITE_STYLE : STREET_STYLE, [satellite]);

  const handleLoad = useCallback((e: MapEvent) => {
    setMapReady(true);
    if (!satellite) {
      for (const id of HIDDEN_LAYERS) {
        if (e.target.getLayer(id)) e.target.removeLayer(id);
      }
    }
    // Detect TomTom tile errors at runtime
    e.target.on('error', (err: any) => {
      if (err?.error?.status === 403 && err?.error?.url?.includes('tomtom.com')) {
        setTrafficAvailable(false);
      }
    });
  }, [satellite]);

  const handleNarrative = useCallback((meta: NarrativeMeta | null) => {
    setInfoMeta(meta);
    if (meta?.alarm) onAlarmMeta?.(meta);
  }, [onAlarmMeta]);

  // Defensa contra "viewport medio negro": fuerza un resize del canvas de
  // MapLibre cuando (a) el contenedor cambia de tamaño y (b) la pestaña
  // vuelve de background. El ResizeObserver interno de MapLibre puede
  // perderse en re-renders (StrictMode) o cuando el navegador suspende la
  // pestaña — el síntoma es el canvas con tamaño desactualizado dejando ver
  // el `bg-gray-950` (#030712) del wrapper.
  useEffect(() => {
    if (!mapReady) return;
    const containerEl = (mapRef.current?.getMap()?.getContainer() ?? null) as HTMLElement | null;
    if (!containerEl) return;
    const resize = () => mapRef.current?.getMap()?.resize();
    const ro = new ResizeObserver(resize);
    ro.observe(containerEl);
    const onVisible = () => {
      if (document.visibilityState === 'visible') resize();
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => {
      ro.disconnect();
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [mapReady]);

  const hideStreetLayers = useCallback((e: MapEvent) => {
    if (satellite) return;
    const map = e.target;
    for (const id of HIDDEN_LAYERS) {
      if (map.getLayer(id)) map.removeLayer(id);
    }
  }, [satellite]);

  return (
    <div className="relative w-full h-full">
      {/* Compact map controls — bottom-right single stack */}
      <div className="absolute bottom-3 right-[10px] z-50 flex flex-col gap-1">
        <button
          onClick={() => mapRef.current?.zoomIn({ duration: 200 })}
          className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white shadow-md rounded text-sm font-bold text-gray-700 cursor-pointer"
        >+</button>
        <button
          onClick={() => mapRef.current?.zoomOut({ duration: 200 })}
          className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white shadow-md rounded text-sm font-bold text-gray-700 cursor-pointer"
        >−</button>
        <button
          onClick={() => setSatellite(s => !s)}
          className="w-7 h-7 flex items-center justify-center bg-white/90 hover:bg-white shadow-md rounded text-[10px] font-bold text-gray-700 cursor-pointer"
          title={satellite ? 'Mapa' : 'Satélite'}
        >{satellite ? 'M' : 'S'}</button>
        {import.meta.env.VITE_TOMTOM_API_KEY && (
          <div data-highlight="traffic-buttons" className="flex flex-col bg-white/90 shadow-md rounded overflow-hidden text-[10px] font-medium text-gray-700 mt-1">
            {([['relative0', 'Rel'], ['absolute', 'Abs']] as const).map(([style, label]) => (
              <button
                key={style}
                onClick={() => setFlowStyle(style)}
                className={`px-1.5 py-1 transition-colors cursor-pointer ${
                  flowStyle === style
                    ? 'bg-blue-500 text-white'
                    : 'hover:bg-gray-200'
                }`}
              >
                {label}
              </button>
            ))}
            {!trafficAvailable && (
              <div className="px-1.5 py-1 text-[8px] text-red-500 text-center border-t border-gray-200" title="API de tráfico no disponible (cuota agotada)">
                sin datos
              </div>
            )}
          </div>
        )}
      </div>
      <Map
        ref={mapRef}
        onLoad={handleLoad}
        onStyleData={hideStreetLayers}
        onClick={(e: MapLayerMouseEvent) => {
          const editMode = useEditMode.getState();
          const coord: [number, number] = [e.lngLat.lng, e.lngLat.lat];
          if (editMode.picking) {
            editMode.stopPicking();
            editMode.setPendingCoord(coord);
          } else if (editMode.addingRegulator) {
            editMode.stopAddingRegulator();
            editMode.setPendingNewRegulatorCoord(coord);
          } else if (editMode.addingSignal) {
            editMode.stopAddingSignal();
            editMode.setPendingNewSignalCoord(coord);
          }
        }}
        cursor={useEditMode(s => (
          s.picking || s.pickingSignal || s.addingRegulator || s.addingSignal ? 'crosshair' : 'auto'
        ))}
        initialViewState={{
          longitude: center[0],
          latitude: center[1],
          zoom,
          bearing,
          pitch
        }}
        onMove={(e) => {
          setMapBearing(e.viewState.bearing);
          setMapZoom(e.viewState.zoom);
          onViewState?.([e.viewState.longitude, e.viewState.latitude], e.viewState.zoom, e.viewState.bearing, e.viewState.pitch);
        }}
        style={{ width: '100%', height: '100%' }}
        mapStyle={mapStyle}
        maxBounds={maxBounds}
        minZoom={minZoom}
        maxZoom={maxZoom}
        maxPitch={70}
        attributionControl={false}
      >
        {cameraControl && <CameraController mapRef={mapRef} ready={mapReady} onNarrative={handleNarrative} onAlarm={onCameraAlarm} />}
        <TrafficLayer flowStyle={flowStyle} available={trafficAvailable} />
        <MapBearingContext.Provider value={mapBearing}>
          <MapZoomContext.Provider value={mapZoom}>
            {children}
          </MapZoomContext.Provider>
        </MapBearingContext.Provider>
      </Map>
      {cameraControl && <NarrativeOverlay meta={infoMeta} />}
    </div>
  );
}
